import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuthStore } from '../store/authStore';
import './APIKeys.css';

const APIKeys = () => {
  const { user } = useAuthStore();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyData, setNewKeyData] = useState(null);

  const [formData, setFormData] = useState({
    projectName: '',
    environment: 'test',
    permissions: ['invoice:create', 'invoice:read', 'time:create', 'time:read'],
  });

  const functions = getFunctions();

  const availablePermissions = [
    { value: 'invoice:create', label: 'Create Invoices' },
    { value: 'invoice:read', label: 'Read Invoices' },
    { value: 'payment:read', label: 'Read Payments' },
    { value: 'time:create', label: 'Create Time Entries' },
    { value: 'time:read', label: 'Read Time Entries' },
    { value: 'client:read', label: 'Read Clients' },
  ];

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    setLoading(true);
    try {
      const listKeys = httpsCallable(functions, 'listApiKeys');
      const result = await listKeys();
      setApiKeys(result.data.apiKeys || []);
    } catch (error) {
      console.error('Error loading API keys:', error);
      alert('Error loading API keys: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const create = httpsCallable(functions, 'createApiKey');
      const result = await create(formData);

      setNewKeyData(result.data);
      setShowCreateForm(false);
      setFormData({
        projectName: '',
        environment: 'test',
        permissions: ['invoice:create', 'invoice:read', 'time:create', 'time:read'],
      });

      await loadApiKeys();
    } catch (error) {
      console.error('Error creating API key:', error);
      alert('Error creating API key: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const revokeApiKey = async (apiKey) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const revoke = httpsCallable(functions, 'revokeApiKey');
      await revoke({ apiKey });
      alert('API key revoked successfully');
      await loadApiKeys();
    } catch (error) {
      console.error('Error revoking API key:', error);
      alert('Error revoking API key: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permission) => {
    setFormData((prev) => {
      const permissions = prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission];
      return { ...prev, permissions };
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('API key copied to clipboard!');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString();
  };

  return (
    <div className="api-keys-page">
      <div className="page-header">
        <h1>🔑 API Keys</h1>
        <p>Manage API keys for external project integrations</p>
        <button className="create-key-btn" onClick={() => setShowCreateForm(true)}>
          + Create New API Key
        </button>
      </div>

      {newKeyData && (
        <div className="new-key-alert">
          <h3>⚠️ Save Your API Key</h3>
          <p>This is the only time you'll see this key. Store it securely!</p>
          <div className="key-display">
            <code>{newKeyData.apiKey}</code>
            <button onClick={() => copyToClipboard(newKeyData.apiKey)}>Copy</button>
          </div>
          <button className="close-alert" onClick={() => setNewKeyData(null)}>
            I've saved it
          </button>
        </div>
      )}

      {showCreateForm && (
        <div className="modal-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create New API Key</h2>
            <form onSubmit={createApiKey}>
              <div className="form-group">
                <label>Project Name *</label>
                <input
                  type="text"
                  value={formData.projectName}
                  onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                  placeholder="My External App"
                  required
                />
              </div>

              <div className="form-group">
                <label>Environment</label>
                <select
                  value={formData.environment}
                  onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                >
                  <option value="test">Test</option>
                  <option value="production">Production</option>
                </select>
              </div>

              <div className="form-group">
                <label>Permissions</label>
                <div className="permissions-list">
                  {availablePermissions.map((perm) => (
                    <label key={perm.value} className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(perm.value)}
                        onChange={() => togglePermission(perm.value)}
                      />
                      {perm.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create API Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="api-keys-list">
        {loading && apiKeys.length === 0 ? (
          <p>Loading API keys...</p>
        ) : apiKeys.length === 0 ? (
          <div className="empty-state">
            <p>No API keys yet. Create one to integrate external projects.</p>
          </div>
        ) : (
          <table className="keys-table">
            <thead>
              <tr>
                <th>Project Name</th>
                <th>API Key</th>
                <th>Environment</th>
                <th>Permissions</th>
                <th>Created</th>
                <th>Last Used</th>
                <th>Usage</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((key) => (
                <tr key={key.apiKey}>
                  <td>{key.projectName}</td>
                  <td>
                    <code className="masked-key">{key.apiKey}</code>
                  </td>
                  <td>
                    <span className={`env-badge ${key.environment}`}>
                      {key.environment}
                    </span>
                  </td>
                  <td>
                    <div className="permissions-tags">
                      {key.permissions.slice(0, 2).map((perm) => (
                        <span key={perm} className="perm-tag">
                          {perm.split(':')[1]}
                        </span>
                      ))}
                      {key.permissions.length > 2 && (
                        <span className="perm-tag">+{key.permissions.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td>{formatDate(key.createdAt)}</td>
                  <td>{formatDate(key.lastUsed)}</td>
                  <td>{key.usageCount || 0} requests</td>
                  <td>
                    <span className={`status-badge ${key.enabled ? 'active' : 'disabled'}`}>
                      {key.enabled ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td>
                    {key.enabled && (
                      <button
                        className="revoke-btn"
                        onClick={() => revokeApiKey(key.fullKey)}
                        disabled={loading}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="api-documentation">
        <h2>📚 Quick Start Guide</h2>
        <div className="code-example">
          <h3>Creating an Invoice</h3>
          <pre>{`const response = await fetch('https://YOUR_REGION-YOUR_PROJECT.cloudfunctions.net/createInvoice', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    data: {
      apiKey: 'YOUR_API_KEY',
      clientId: 'client-123',
      lineItems: [
        { description: 'Web Development', quantity: 10, rate: 100 }
      ],
      dueDate: '2024-02-15',
      notes: 'Project ABC invoice'
    }
  })
});

const result = await response.json();
console.log(result.result);`}</pre>
        </div>

        <div className="code-example">
          <h3>Recording Time Entry</h3>
          <pre>{`const response = await fetch('https://YOUR_REGION-YOUR_PROJECT.cloudfunctions.net/recordTimeEntry', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    data: {
      apiKey: 'YOUR_API_KEY',
      clientId: 'client-123',
      projectId: 'project-456',
      description: 'Feature development',
      duration: 7200, // seconds (2 hours)
      rate: 100
    }
  })
});`}</pre>
        </div>
      </div>
    </div>
  );
};

export default APIKeys;
